/**
 * useHierarchieChantiers.ts — S229 lot 7d (PLAN.MATCH.1, DEC-CHANTIER-E8-1 ; S229-PLAN-LOTS-7.md v1.2, section 2, E8-5)
 * Les projets, missions et tâches réels d'un chantier, lus par les routes de lecture existantes (GET /api/v1/projets, /missions-user,
 * /taches-user, filtrées par chantier et par tenant), dans la forme du parcours de ChantierView (chantier → projets → missions → tâches).
 * Sans écriture dans la V2 gelée (REG-V2-ARRET-1). Et le chantier à ouvrir depuis la Discussion (« Voir le chantier », Q-CAI-4 : sessionStorage,
 * clé sans « ghostx », lue puis effacée).
 */
import { useEffect, useState } from "react";
import type { MockProjetItem, MockMissionItem, MockTacheItem } from "../data/mock/chantiers.mock";
import { entetesDiscussion } from "./useGestesDiscussion";

export const CLE_CHANTIER_A_OUVRIR = "ub-chantier-a-ouvrir";

/** Le chantier demandé par « Voir le chantier » (id de l'API), lu une seule fois ; null s'il n'y en a pas */
export function lireChantierAOuvrir(): number | null {
  try {
    const v = sessionStorage.getItem(CLE_CHANTIER_A_OUVRIR);
    sessionStorage.removeItem(CLE_CHANTIER_A_OUVRIR);
    const n = v ? parseInt(v, 10) : NaN;
    return Number.isNaN(n) ? null : n;
  } catch { return null; }
}

export function demanderChantierAOuvrir(chantierId: number): void {
  try { sessionStorage.setItem(CLE_CHANTIER_A_OUVRIR, String(chantierId)); } catch { /* stockage indisponible : la liste s'ouvre */ }
}

type Ligne = Record<string, unknown> & { id: number; titre?: string };
const txt = (v: unknown) => (v === null || v === undefined ? "" : String(v));

/** Fonction pure : projets → missions (par projet_id) → tâches (par mission_id), dans la forme de ChantierView */
export function hierarchieDuChantier(projets: Ligne[], missions: Ligne[], taches: Ligne[]): MockProjetItem[] {
  const tachesDe = (mid: number): MockTacheItem[] => taches.filter((t) => t.mission_id === mid).map((t) => ({
    id: t.id, titre: txt(t.titre), description: txt(t.description), phase: "discussion", progression: t.status === "terminee" ? 100 : 0,
    assignee: txt(t.assignee_nom), echeance: txt(t.echeance), documents: [], jalons: [] }));
  const missionsDe = (pid: number): MockMissionItem[] => missions.filter((m) => m.projet_id === pid).map((m) => ({
    id: m.id, titre: txt(m.titre), description: txt(m.description), phase: "discussion", progression: 0, botPrimaire: txt(m.bot_primaire) || "CEOB",
    echeance: txt(m.echeance), livrables: [], documents: [], jalons: [], taches: tachesDe(m.id) }));
  return projets.map((p) => ({
    id: p.id, titre: txt(p.titre), description: txt(p.description), phase: "discussion", progression: Number(p.progression) || 0,
    botPrimaire: txt(p.bot_primaire) || "CEOB", echeance: txt(p.echeance), objectifs: (p.objectifs as string[]) || [], livrables: [], budget: "",
    documents: [], jalons: [], missions: missionsDe(p.id) }));
}

async function lire(route: string): Promise<Ligne[]> {
  try {
    const r = await fetch(route, { headers: entetesDiscussion() });
    const d = r.ok ? await r.json() : [];
    return Array.isArray(d) ? d as Ligne[] : [];
  } catch { return []; }
}

/** Les projets (avec missions et tâches) de chaque chantier de l'API, par id de chantier */
export function useHierarchieChantiers(chantierIds: number[]): Record<number, MockProjetItem[]> {
  const [par, setPar] = useState<Record<number, MockProjetItem[]>>({});
  const cle = chantierIds.join(",");
  useEffect(() => {
    let annule = false;
    void Promise.all(chantierIds.map(async (id) => {
      const [p, m, t] = await Promise.all([lire(`/api/v1/projets?chantier_id=${id}`), lire(`/api/v1/missions-user?chantier_id=${id}`),
        lire(`/api/v1/taches-user?chantier_id=${id}`)]);
      return [id, hierarchieDuChantier(p, m, t)] as const;
    })).then((paires) => { if (!annule) setPar(Object.fromEntries(paires)); });
    return () => { annule = true; };
  }, [cle]);  // eslint-disable-line react-hooks/exhaustive-deps
  return par;
}
